import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router';
import { debounce } from 'lodash';
import { useTrack } from '@hackler/react-sdk';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  CapaDesignServiceType,
  CapaPcbServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import * as asset from '@assets';
import { requestEnumToString } from '@utils/enumToString';
import PannelLayout from '@routes/NewRequest/Layout/PannelLayout';
import RequestCommonLayout from '@routes/NewRequest/Layout/RequestCommonLayout';
import RequestBottomNav from '@routes/NewRequest/Layout/RequestBottomNav';
import {
  DesignRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import RequestPreview from '@routes/NewRequest/RequestPreview';
import DesignProcessSelect from '../Design/DesignProcessSelect';
import DesignReferenceStep from './DesignReferenceStep';
import DesignPrototypingStep from './DesignPrototypingStep';
import CommonStep from './CommonStep';
import DesignHelperDrawer from '@routes/NewRequest/FormComponents/DesignHelperDrawer';
import ConsultingStartMethodStep from '@routes/NewRequest/ConsultingStartMethodStep';

const steps = [
  DesignRequestStageEnum.PROCESS_SELECT,
  DesignRequestStageEnum.REFERENCE,
  DesignRequestStageEnum.PROTOTYPING,
  DesignRequestStageEnum.PROJECT_COMMON,
  DesignRequestStageEnum.CONSULTING_START_METHOD,
  DesignRequestStageEnum.PREVIEW,
  DesignRequestStageEnum.COMPLETE
];

const DesignStepRequestPresenter = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [designHelperDrawerOpen, setDesignHelperDrawerOpen] = useState<boolean>(
    false
  );
  const [buttonStateHandler, setButtonStateHandler] = useState<{
    disableHandler: (value: boolean) => void;
  }>({
    disableHandler: (value) => undefined
  });
  const currentEventState = useRef<{
    selectedProcess: string;
    exitStage: string;
  }>({
    selectedProcess: '',
    exitStage: ''
  });
  const nextButtonAction = useRef<
    Partial<Record<DesignRequestStageEnum, () => void>>
  >({
    PROCESS_SELECT: () => undefined,
    REFERENCE: () => undefined,
    PROTOTYPING: () => undefined,
    PROJECT_COMMON: () => undefined,
    PREVIEW: () => undefined
  });
  const {
    query: { process, stage },
    onAddQueryString
  } = useQueryString<RequestQueryString & { stage: DesignRequestStageEnum }>();
  const history = useHistory<{ fromLandingPage?: boolean }>();
  const track = useTrack();
  const processArray = useMemo(() => process?.split(',') ?? [], [process]);
  const currentStage = useRef<DesignRequestStageEnum | null>(null);
  const lastStage = useRef<DesignRequestStageEnum | null>(null);
  const currentProcess = useRef<string | null>(null);
  const buttonAction =
    nextButtonAction.current?.[stage as DesignRequestStageEnum];

  const currentStageIndex = steps.findIndex((step) => step === stage);
  const currentStep = useMemo(() => {
    switch (stage) {
      case DesignRequestStageEnum.PROCESS_SELECT:
        return {
          content: (
            <DesignProcessSelect
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: '필요한 작업을 모두 선택해주세요.'
        };

      case DesignRequestStageEnum.REFERENCE:
        return {
          content: (
            <DesignReferenceStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `참고할 파일을
              첨부해주세요.`
        };

      case DesignRequestStageEnum.PROTOTYPING:
        return {
          content: (
            <DesignPrototypingStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `시제품 제작 정보를
              입력해주세요.`
        };

      case DesignRequestStageEnum.PROJECT_COMMON:
        return {
          content: (
            <CommonStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `프로젝트 정보를
                입력해주세요.`
        };

      case DesignRequestStageEnum.CONSULTING_START_METHOD:
        return {
          content: (
            <ConsultingStartMethodStep
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `상담 시작 방식을 선택해주세요.`
        };

      case DesignRequestStageEnum.PREVIEW:
        return {
          content: (
            <RequestPreview
              setDisabledNextButton={buttonStateHandler.disableHandler}
              nextButtonAction={nextButtonAction}
            />
          ),
          title: `마지막으로 요청서를 확인해주세요.`,
          formId: 'preview'
        };
    }
  }, [stage, buttonStateHandler.disableHandler]);

  const extraDescription = useMemo(() => {
    if (!processArray) return;
    if (processArray) {
      const designOrder: string[] = [
        CapaDesignServiceType.ProductDesign,
        CapaDesignServiceType.MechanicalDesign
      ];

      return designOrder
        .reduce((acc, service) => {
          if (processArray.includes(service)) {
            acc.push(requestEnumToString(service as CapaPcbServiceType));
          }
          return acc;
        }, [] as string[])
        .join(', ');
    }
  }, [processArray]);

  const onMoveNextStage = useCallback(() => {
    if (
      stage !== DesignRequestStageEnum.COMPLETE &&
      stage !== DesignRequestStageEnum.PREVIEW
    ) {
      onAddQueryString({
        stage: steps[currentStageIndex + 1]
      });
    }
    buttonAction && buttonAction();
  }, [onAddQueryString, currentStageIndex, buttonAction, stage]);

  const onMovePrevStage = useCallback(() => {
    if (currentStageIndex === 0) {
      history.push('/request');
    } else {
      onAddQueryString({
        stage: steps[currentStageIndex - 1]
      });
    }

    if (stage !== DesignRequestStageEnum.PREVIEW) {
      buttonAction && buttonAction();
    }
  }, [onAddQueryString, currentStageIndex, history, stage, buttonAction]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (stage) {
      currentStage.current = stage;
    }
  }, [stage]);

  useEffect(() => {
    if (process) {
      currentProcess.current = process;
    }
  }, [process]);

  useEffect(() => {
    currentEventState.current = {
      exitStage: !lastStage.current
        ? ('' as DesignRequestStageEnum)
        : lastStage.current,
      selectedProcess: currentProcess.current ?? ''
    };
  }, [process, stage]);

  useEffect(() => {
    if (stage) {
      if (!lastStage.current) {
        lastStage.current = stage;
      }

      if (
        steps.findIndex((step) => step === stage) >
        steps.findIndex((step) => step === lastStage.current)
      ) {
        lastStage.current = stage;
      }
    }
  }, [stage]);

  useEffect(() => {
    const current = currentStage.current;

    return () => {
      if (
        current &&
        current !== DesignRequestStageEnum.COMPLETE &&
        lastStage.current !== DesignRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            serviceType: CapaQuotationServiceType.Design,
            exitStage: lastStage.current ?? '',
            selectedProcess: currentProcess.current ?? ''
          }
        });
      }
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const current = currentStage.current;

    const sendTrackingData = () => {
      if (
        current &&
        current !== DesignRequestStageEnum.COMPLETE &&
        lastStage.current !== DesignRequestStageEnum.COMPLETE
      ) {
        track({
          key: 'exit',
          properties: {
            serviceType: CapaQuotationServiceType.Design,
            exitStage: lastStage.current ?? '',
            selectedProcess: currentProcess.current ?? ''
          }
        });
      }
    };

    window.addEventListener('beforeunload', sendTrackingData);

    return () => {
      window.removeEventListener('beforeunload', sendTrackingData);
    };
  });

  return (
    <RequestCommonLayout
      matches={matches}
      title={currentStep?.title}
      serviceExtraDescription={
        stage !== DesignRequestStageEnum.PROCESS_SELECT
          ? extraDescription
          : undefined
      }
      titleBackgroundImage={
        matches ? asset.ServiceType_Md : asset.DesignReqeustAsideBackground
      }
      subButtonLabel={
        stage === DesignRequestStageEnum.PROCESS_SELECT
          ? '제품디자인/기구설계가 궁금하다면?'
          : undefined
      }
      onClickSubButton={
        stage === DesignRequestStageEnum.PROCESS_SELECT
          ? () => setDesignHelperDrawerOpen(true)
          : undefined
      }
      onClickClose={() => history.push('/request')}
    >
      <DesignHelperDrawer
        open={designHelperDrawerOpen}
        onClose={() => setDesignHelperDrawerOpen(false)}
        matches={matches}
      />
      <PannelLayout
        content={currentStep?.content}
        bottomNav={
          stage && (
            <RequestBottomNav
              stage={stage}
              setButtonStateHandler={setButtonStateHandler}
              onClickBack={debounce(onMovePrevStage, 50)}
              onClickNext={debounce(onMoveNextStage, 50)}
              steps={steps}
            />
          )
        }
      />
    </RequestCommonLayout>
  );
};

export default DesignStepRequestPresenter;
